"use client";

import React, { useState, useEffect } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import VideoPlayer from "/components/VideoPlayer/VideoPlayer";
import styles from "./PerspectiveManagerDisplay.module.css";

const PERSPECTIVES = ["front", "side", "rear", "45_degree"];

/**
 * File: /components/VideoPlayer/VideoPlayer.jsx (example)
 * (Your VideoPlayer component should initialize and render a video player using video.js.)
 *
 * Props:
 * - src: the video source URL.
 * - preload: video preload attribute.
 * - initialPlaybackRate: initial playback rate.
 * - preloadDuration: duration to preload.
 * - onLoadedMetadata: callback when metadata is loaded.
 * - hidePlaybackControls: boolean to hide custom controls.
 * - playbackRate: current playback rate.
 * - setPlaybackRate: callback to update playback rate.
 *
 * (Your implementation may vary.)
 */

const PerspectiveManagerDisplay = ({ content }) => {
  const perspectives = PERSPECTIVES;

  // State to store videos per perspective. Initially, an empty object.
  const [videos, setVideos] = useState({});
  // State to track which perspective's section is expanded (all collapsed by default).
  const [expandedViews, setExpandedViews] = useState({});
  // State to track the playback rate for each perspective.
  // Each perspective's playback rate is initialized to 1.0.
  const [playbackRates, setPlaybackRates] = useState({
    front: 1.0,
    side: 1.0,
    rear: 1.0,
    "45_degree": 1.0,
  });

  // Fetch videos for each perspective only once.
  useEffect(() => {
    if (!content?.content_id) return;
    perspectives.forEach((perspective) => {
      // Only fetch if we haven't set a value for this perspective yet.
      if (videos[perspective] === undefined) {
        fetch(
          `/api/content/${content.content_id}/perspectives?perspective=${perspective}`
        )
          .then((res) => res.json())
          .then((data) => {
            console.log(`Fetched videos for ${perspective}:`, data.videos);
            setVideos((prev) => ({
              ...prev,
              [perspective]: data.videos || [],
            }));
          })
          .catch((err) => {
            console.error(`Error fetching videos for ${perspective}:`, err);
            setVideos((prev) => ({ ...prev, [perspective]: [] }));
          });
      }
    });
    // We intentionally do not include 'videos' in the dependency array to avoid re-fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.content_id, perspectives]);

  // Initialize video.js players for visible video elements when a section is expanded.
  useEffect(() => {
    const players = {};
    perspectives.forEach((perspective) => {
      if (expandedViews[perspective]) {
        (videos[perspective] || []).forEach((video) => {
          const playerId = `video-player-${video.id}`;
          const videoElement = document.getElementById(playerId);
          if (videoElement) {
            players[playerId] = videojs(videoElement, {
              controls: true,
              preload: "auto",
              responsive: true,
              fluid: true,
            });
            console.log(`Initialized video.js for ${playerId}`);
          } else {
            console.log(`No video element found for ${playerId}`);
          }
        });
      }
    });
    // Trigger a resize event after a short delay to force layout recalculation.
    setTimeout(() => window.dispatchEvent(new Event("resize")), 200);
    return () => {
      Object.values(players).forEach((player) => {
        if (player) player.dispose();
      });
    };
  }, [videos, expandedViews, perspectives]);

  // When a perspective is toggled open, force a reload of its videos.
  useEffect(() => {
    perspectives.forEach((perspective) => {
      if (
        expandedViews[perspective] &&
        videos[perspective] &&
        videos[perspective].length > 0
      ) {
        setTimeout(() => {
          videos[perspective].forEach((video) => {
            const elem = document.getElementById(`video-player-${video.id}`);
            if (elem) {
              console.log(
                `Forcing reload for video ${video.id} in ${perspective} view`
              );
              elem.load();
              // Optionally auto-play:
              elem
                .play()
                .catch((err) => console.error("Error auto-playing video:", err));
            }
          });
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    });
  }, [expandedViews, videos, perspectives]);

  // Toggle the expanded/collapsed state for a perspective.
  const toggleView = (perspective) => {
    setExpandedViews((prev) => ({
      ...prev,
      [perspective]: !prev[perspective],
    }));
    window.dispatchEvent(new Event("resize"));
  };

  // Handler to update the playback rate for a given perspective.
  const updatePlaybackRate = (perspective, newRate) => {
    setPlaybackRates((prev) => ({ ...prev, [perspective]: newRate }));
  };

  return (
    <div className={styles.perspectiveManagerDisplay}>
      <p>This content item is documented from various perspectives.</p>
      {perspectives.map((perspective) => {
        const perspectiveVideos = videos[perspective] || [];
        return (
          <div key={perspective} className={styles.perspectiveRow}>
            <div
              className={styles.perspectiveHeader}
              onClick={() => toggleView(perspective)}
            >
              <h3>
                {perspective.charAt(0).toUpperCase() + perspective.slice(1)}{" "}
                View
              </h3>
              <span className={styles.toggleIcon}>
                {expandedViews[perspective] ? "-" : "+"}
              </span>
            </div>
            {expandedViews[perspective] && (
              <div className={styles.perspectiveContent}>
                {perspectiveVideos.length > 0 ? (
                  perspectiveVideos.map((video) => (
                    <VideoPlayer
                      key={video.id}
                      src={video.video_path}
                      preload="auto"
                      initialPlaybackRate={1.0}
                      preloadDuration={5}
                      onLoadedMetadata={() =>
                        window.dispatchEvent(new Event("resize"))
                      }
                      hidePlaybackControls={false}
                      playbackRate={playbackRates[perspective]}
                      setPlaybackRate={(newRate) =>
                        updatePlaybackRate(perspective, newRate)
                      }
                    />
                  ))
                ) : (
                  <p>No video uploaded.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PerspectiveManagerDisplay;
